/**
 * ConversationsPanel — standalone tab for branch-scoped threaded comments.
 *
 * Supports three targets:
 *   - A selected DAG node (from URL ?nodes= param)
 *   - A selected DAG edge (from URL ?edge= param)
 *   - Project-level (when nothing is selected)
 */

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Box, Flex, Heading, Separator, Text } from "@radix-ui/themes";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useProvenance } from "../../hooks/useProvenance";
import CommentsThread from "../comments/CommentsThread";

const KIND_COLORS: Record<string, "blue" | "violet" | "cyan" | "green" | "amber" | "gray"> = {
  geometry: "blue",
  surface_mesh: "violet",
  volume_mesh: "cyan",
  run: "green",
  saved_plot: "amber",
};

export default function ConversationsPanel() {
  const { projectId, currentBranch, selectedNodeIds } = useProvenanceContext();
  const { data } = useProvenance(projectId, currentBranch);
  const [searchParams] = useSearchParams();

  const edgeId = searchParams.get("edge");

  const primaryNodeId = useMemo(() => {
    if (selectedNodeIds.size === 0) return null;
    return [...selectedNodeIds][0]!;
  }, [selectedNodeIds]);

  const node = useMemo(() => {
    if (!primaryNodeId || !data?.nodes) return null;
    return data.nodes.find((n) => n.id === primaryNodeId) ?? null;
  }, [primaryNodeId, data?.nodes]);

  const edge = useMemo(() => {
    if (!edgeId || !data?.edges) return null;
    return data.edges.find((e) => e.id === edgeId) ?? null;
  }, [edgeId, data?.edges]);

  const edgeLabel = useMemo(() => {
    if (!edge || !data?.nodes) return null;
    const src = data.nodes.find((n) => n.id === edge.parent_id);
    const tgt = data.nodes.find((n) => n.id === edge.child_id);
    return `${src?.name ?? "?"} \u2192 ${tgt?.name ?? "?"}`;
  }, [edge, data?.nodes]);

  // Edge takes priority when present, then node, then project
  let targetType: string;
  let targetId: string;
  if (edge) {
    targetType = "edge";
    targetId = edge.id;
  } else if (node) {
    targetType = "node";
    targetId = node.id;
  } else {
    targetType = "project";
    targetId = projectId;
  }

  return (
    <Flex direction="column" p="4" style={{ height: "100%" }} data-tour="conversations-panel">
      <Flex align="center" gap="2" mb="3" flexShrink="0">
        <svg width="18" height="18" viewBox="0 0 15 15" fill="none">
          <path d="M12.5 3h-10a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1H5l2.5 2.5L10 11h2.5a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1Z" stroke="currentColor" strokeWidth="1.2" />
        </svg>
        <Heading size="3">Conversations</Heading>
      </Flex>

      {edge ? (
        <Flex align="center" gap="2" mb="2" flexShrink="0">
          <Text size="1" color="gray">On edge:</Text>
          <Badge size="1" color="gray" variant="soft">
            {edge.relationship ?? "edge"}
          </Badge>
          <Text size="2" weight="medium">{edgeLabel}</Text>
        </Flex>
      ) : node ? (
        <Flex align="center" gap="2" mb="2" flexShrink="0">
          <Text size="1" color="gray">On:</Text>
          <Badge size="1" color={KIND_COLORS[node.kind] ?? "gray"} variant="soft">
            {node.kind}
          </Badge>
          <Text size="2" weight="medium">{node.name}</Text>
        </Flex>
      ) : (
        <Text size="1" color="gray" mb="2">
          Project-level conversations. Select a node or right-click an edge to see its threads.
        </Text>
      )}

      <Separator size="4" mb="3" />

      <Box style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <CommentsThread
          projectId={projectId}
          targetType={targetType}
          targetId={targetId}
          branch={currentBranch}
        />
      </Box>
    </Flex>
  );
}
